import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash/lang';

import { PatientEncounterReviewApi } from '../../../../../api';
import { useCurrentRoute } from '../../../../root/router';

import ReviewDataResolver from './ReviewDataResolver';

export const ReviewContext = React.createContext(null);

export function ReviewProvider({ children }) {
  const currentRoute = useCurrentRoute();
  const params = currentRoute.params;

  const { ssuPatientId, patientEncounterId } = params;
  const [itemGroupSnapshotReviewStates, setItemGroupSnapshotReviewStates] = useState({});
  const [logSnapshotReviewStates, setLogSnapshotReviewStates] = useState({});
  const [chosenItem, setChosenItem] = useState();
  const [logItemGroups, setLogItemGroups] = useState([]);
  const [logCheckSnapshotReviewStates, setLogCheckSnapshotReviewStates] = useState({});
  const [logCheckItemGroups, setLogCheckItemGroups] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);
  const [signatureId, setSignatureId] = useState(null);
  const isHistoricalData = useMemo(() => getIsHistoricalData(signatureId), [signatureId]);
  const [reviewMetaData, setReviewMetaData] = useState(null);
  const reviewContentRefContainer = useRef(null);
  const loadData = useCallback(
    function reloadItemGroupsAndLogs() {
      PatientEncounterReviewApi.getCraReview(ssuPatientId, patientEncounterId).then(({ data }) => {
        setReviewMetaData(data.reviewMetaData);
        setItemGroups(data.patientEncounterStructure.itemGroups.map(({ itemGroupRef }) => itemGroupRef));
        setLogItemGroups(sortLogItemGroups(data.logsResponse.logItemGroups, (a, b) => (a.name > b.name && 1) || -1));
        setLogCheckItemGroups(
          sortLogItemGroups(
            sortLogItemGroups(
              data?.logChecksResponse?.logCheckItemGroups,
              (a, b) => (a.patientItemGroupName > b.patientItemGroupName && 1) || -1
            )
          )
        );
      });
    },
    [ssuPatientId, patientEncounterId]
  );

  const sortLogItemGroups = function sortItems(items, compare) {
    return items && items?.length > 1 ? items.sort(compare) : items;
  };

  useEffect(loadData, [loadData]);

  const getItemGroupData = useCallback(
    function(patientItemGroupId, hideLoader = true) {
      function itemGroupsSnapshotGetFromResponse({ data }) {
        setItemGroupSnapshotReviewStates(prevState => ({
          ...prevState,
          [data.itemGroupSnapshotState.itemGroupRef.patientItemGroupId]: data
        }));
      }

      ReviewDataResolver.getItemGroupRowSnapshotReviewStateBySignatureId(
        signatureId,
        {
          ssuPatientId,
          patientEncounterId,
          itemGroupId: patientItemGroupId
        },
        hideLoader
      ).then(itemGroupsSnapshotGetFromResponse);
    },
    [patientEncounterId, signatureId, ssuPatientId]
  );

  useEffect(
    function reloadItemGroupSnapshotReviewStates() {
      if (isEmpty(itemGroups)) {
        return;
      }
      itemGroups.forEach(item => {
        getItemGroupData(item.patientItemGroupId);
      });
      return function() {
        setItemGroupSnapshotReviewStates({});
      };
    },
    [itemGroups, signatureId, patientEncounterId, ssuPatientId, getItemGroupData]
  );

  const getLogData = useCallback(
    function(itemGroupTemplateId, hideLoader = true) {
      function logSnapshotGetFromResponse({ data }) {
        setLogSnapshotReviewStates(prevState => ({
          ...prevState,
          [data.logSnapshotState.logFormRef.itemGroupTemplateId]: data
        }));
      }

      ReviewDataResolver.getLogSnapshotReviewStateBySignatureId(
        signatureId,
        {
          ssuPatientId,
          patientEncounterId,
          itemGroupTemplateId: itemGroupTemplateId
        },
        hideLoader
      ).then(logSnapshotGetFromResponse);
    },
    [patientEncounterId, signatureId, ssuPatientId]
  );

  useEffect(
    function reloadLogSnapshotReviewStates() {
      if (isEmpty(logItemGroups)) {
        return;
      }
      logItemGroups.forEach(item => {
        getLogData(item.itemGroupTemplateId);
      });
      return function() {
        setLogSnapshotReviewStates({});
      };
    },
    [logItemGroups, signatureId, patientEncounterId, ssuPatientId, getLogData]
  );

  const getLogCheckData = useCallback(
    function(patientItemGroupId, itemGroupTemplateId, hideLoader = true) {
      function logCheckSnapshotGetFromResponse({ data }) {
        setLogCheckSnapshotReviewStates(prevState => ({
          ...prevState,
          [itemGroupTemplateId]: data
        }));
      }
      ReviewDataResolver.getLogCheckSnapshotReviewStateBySignatureId(
        signatureId,
        {
          ssuPatientId,
          patientEncounterId,
          patientItemGroupId
        },
        hideLoader
      ).then(logCheckSnapshotGetFromResponse);
    },
    [patientEncounterId, signatureId, ssuPatientId]
  );

  useEffect(
    function reloadLogCheckSnapshotReviewStates() {
      if (isEmpty(logCheckItemGroups)) {
        return;
      }
      logCheckItemGroups.forEach(item => {
        getLogCheckData(item.patientItemGroupId, item.itemGroupTemplateId);
      });
      return function() {
        setLogCheckSnapshotReviewStates({});
      };
    },
    [logCheckItemGroups, signatureId, patientEncounterId, ssuPatientId, getLogCheckData]
  );

  return (
    <ReviewContext.Provider
      value={{
        itemGroups,
        logItemGroups,
        logCheckItemGroups,
        chosenItem,
        setChosenItem,
        logSnapshotReviewStates,
        logCheckSnapshotReviewStates,
        itemGroupSnapshotReviewStates,
        signatureId,
        setSignatureId,
        ssuPatientId,
        patientEncounterId,
        isHistoricalData,
        reviewMetaData,
        reviewContentRefContainer
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
}

function getIsHistoricalData(signatureId) {
  return !!signatureId;
}
